import { Spacing } from '@persona-id/design-tokens';
import { ClipboardIcon } from '@persona-id/design-tokens/icons';
import { ReactNode } from 'react';
import { Field } from 'redux-form';
import styled from 'styled-components';

import { FieldWrapper } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import { getVtmplvDisplayName } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/utils';
import InputField from 'dashboard/components/FormControls/InputField';
import { MonoSpan } from 'dashboard/components/Typography';
import { VERIFICATION_TEMPLATE_VERSION_TYPE_NAMES_SHORT } from 'dashboard/lib/constants/verification';
import copyToClipboard from 'dashboard/lib/utils/copyToClipboard';
import { VerificationTemplateVersion } from 'dashboard/models/verification-template-version';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import DropdownMenu from 'design-system/components/DropdownMenu';
import Popover from 'design-system/components/Popover';
import { Label, Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { fadeIn } from 'design-system/variables/keyframes';
import { TransitionDuration } from 'design-system/variables/transitions';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';

const CopyButton = styled(Button).attrs(() => ({
  type: 'button',
  variant: 'inverse',
  width: 'icon',
  size: 'sm',
}))`
  svg {
    width: 14px;
    height: 14px;
  }
`;

const StyledCard = styled(Card)`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${Spacing[1]};
  animation: ${fadeIn} ${TransitionDuration.Medium} ease-in-out;
  padding: 6px;
  margin: ${Spacing[1]};
`;

export const StyledId = styled(MonoSpan).attrs(() => ({ size: 'md' }))`
  color: ${({ theme }) => theme.color.text.tertiary};
  margin-left: ${Spacing[2]};
  margin-right: ${Spacing[2]};
  display: inline-block;
  width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledLabel = styled(Label).attrs(() => ({ size: 'lg', weight: 'bold' }))`
  line-height: ${Spacing[5]};
  overflow: hidden;
  text-overflow: ellipsis;

  span:first-child {
    white-space: nowrap;
  }
  span:not(:first-child) {
    font-weight: ${({ theme }) => theme.text.paragraph.md.fontWeight};
  }
`;

const VerificationHeader = styled.div`
  margin-bottom: ${Spacing[1]};
  display: inline-flex;
  align-items: center;
`;

// TODO: [Documents] Migrate to PDS component for underline tooltip indicators when available
const TooltipIndicatorWrapper = styled.div`
  text-decoration: underline;
  text-decoration-style: dotted;
`;

type VerificationTemplateHeadSectionProps = {
  vtmplVersion: VerificationTemplateVersion;
  linkedStepCallout?: ReactNode;
  editable: boolean;
};

const VerificationTemplateHeadSection = ({
  vtmplVersion,
  linkedStepCallout,
  editable,
}: VerificationTemplateHeadSectionProps) => {
  const vtmplVersionId = vtmplVersion.id;
  const vtmplId = vtmplVersion.relationships['verification-template']?.data?.id;
  const displayName = getVtmplvDisplayName(vtmplVersion);

  const [confirmationActive, { enable: showConfirmation, disable: hideConfirmation }] =
    useToggle(false);

  const copyTextToClipboard = async (text: string) => {
    const copyWasSuccessful = await copyToClipboard(text);
    if (copyWasSuccessful) {
      showConfirmation();
      setTimeout(() => {
        hideConfirmation();
      }, 1200);
    }
  };

  return (
    <Card>
      <Card.Body>
        <VerificationHeader>
          <StyledLabel>{displayName}</StyledLabel>
          <StyledId>{vtmplId ?? 'Unsaved'}</StyledId>
          {vtmplId != null && (
            /** omit copy controls when vtmplId is not present as the vtmplv is newly added from a module and hasn't been saved yet. */
            <div>
              {/*Anchor the popover over a zero-width div aligned horizontally with the below button*/}
              <Popover open={confirmationActive} trigger={<div style={{ margin: '0 auto' }} />}>
                <Popover.Content side='top' align='center' style={{ zIndex: 2 }}>
                  <StyledCard elevated>
                    <Paragraph>Copied</Paragraph>
                  </StyledCard>
                </Popover.Content>
              </Popover>
              <DropdownMenu
                trigger={
                  <CopyButton aria-label='Copy'>
                    <ClipboardIcon />
                  </CopyButton>
                }
              >
                {vtmplId != null && (
                  <DropdownMenu.Item
                    id='copy-token__verification-template'
                    aria-label='Copy Verification Template ID'
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      copyTextToClipboard(vtmplId);
                    }}
                  >
                    Copy Verification Template ID
                  </DropdownMenu.Item>
                )}
                <DropdownMenu.Item
                  id='copy-token__verification-template-version'
                  aria-label='Copy Verification Template Version ID'
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    copyTextToClipboard(vtmplVersionId);
                  }}
                >
                  Copy Verification Template Version ID
                </DropdownMenu.Item>
              </DropdownMenu>
            </div>
          )}
        </VerificationHeader>
        <FieldWrapper>
          <Field
            type='text'
            name='attributes.name'
            component={InputField}
            disabled={!editable}
            label='Verification template name'
            placeholder='Enter verification template name'
            data-cy={`verification-template-version-name-input-${
              VERIFICATION_TEMPLATE_VERSION_TYPE_NAMES_SHORT[vtmplVersion.type]
            }`}
            id={`verification-template-name-${vtmplVersionId}`}
          />
        </FieldWrapper>
        {vtmplVersion.type === ApiModel.VerificationTemplateVersionDocument && (
          <FieldWrapper>
            <Field
              type='text'
              name='attributes.document-kind'
              component={InputField}
              disabled={!editable}
              label={
                <Tooltip
                  side='right'
                  trigger={
                    <TooltipIndicatorWrapper>Document type display label</TooltipIndicatorWrapper>
                  }
                >
                  <Paragraph>
                    Specify a display label that best describes the template use case. This label
                    will be displayed in the verifications overview page.
                  </Paragraph>
                </Tooltip>
              }
              placeholder='Enter document type display label'
              data-cy={`verification-template-version-document-kind-input-${
                VERIFICATION_TEMPLATE_VERSION_TYPE_NAMES_SHORT[vtmplVersion.type]
              }`}
              id={`verification-template-document-kind-${vtmplVersionId}`}
            />
          </FieldWrapper>
        )}
        {linkedStepCallout != null && linkedStepCallout}
      </Card.Body>
    </Card>
  );
};

export default VerificationTemplateHeadSection;
